import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext, socket } from "../../provider/AppContext";
import PanelShell from "../Notifications/PanelShell";
import PanelAvatar from "../Notifications/PanelAvatar";
import { shortWhen } from "../Notifications/notificationMeta";
import moment from "moment";

/**
 * Online first (most recent activity first), then everyone who has logged out
 * (most recent logout first), so whoever is working now is always on top.
 */
const sortByPresence = (activeUsersSummary) => {
  const out = activeUsersSummary.filter((u) => u.logoutTime);
  const inn = activeUsersSummary.filter((u) => !u.logoutTime);
  out?.sort((a, b) => moment(b.logoutTime).diff(moment(a.logoutTime)));
  inn?.sort((a, b) =>
    moment(b.lastActivityAt || b.loginTime).diff(
      moment(a.lastActivityAt || a.loginTime)
    )
  );
  return [...inn, ...out];
};

const fullName = (user) =>
  `${user?.employee?.firstName || "Anonymous"} ${
    user?.employee?.lastName || "User"
  }`;

// Active / idle / offline (#264). A user is "idle" once idleTimeoutMinutes
// has passed since their last real interaction (lastActivityAt, falling back
// to loginTime for rows written before that column existed) - display-only,
// their socket stays connected regardless.
const statusOf = (user, idleTimeoutMinutes) => {
  if (user.logoutTime) return "offline";
  const lastActive = new Date(user.lastActivityAt || user.loginTime).getTime();
  const idleMs = idleTimeoutMinutes * 60000;
  return Date.now() - lastActive > idleMs ? "idle" : "active";
};

const STATUS_LABEL = {
  active: "Active now",
  idle: "Idle",
  offline: "Offline",
};

export const ActiveUsers = ({ activeModal, setActiveModal }) => {
  const { activeUsersSummary, idleTimeoutMinutes } = useContext(AppContext);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  // Idle is a pure clock transition with no corresponding socket event, so
  // nothing re-renders this panel on its own when someone crosses the
  // threshold while it's open - force a re-check periodically, but only
  // while the panel is actually open. The backend only broadcasts on an
  // idle -> active flip (not on every ping), so the tick also asks the server
  // for a fresh snapshot for this socket alone; that keeps the timestamps
  // honest at the cost of one message per open panel instead of a broadcast
  // to every client per user per minute.
  const [tick, forceTick] = useState(0);
  useEffect(() => {
    if (!activeModal) return undefined;
    socket.emit("presence_snapshot");
    const interval = setInterval(() => {
      socket.emit("presence_snapshot");
      forceTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [activeModal]);

  const sorted = useMemo(
    () => sortByPresence(activeUsersSummary || []),
    [activeUsersSummary]
  );

  const withStatus = useMemo(
    () =>
      sorted.map((user) => ({
        user,
        status: statusOf(user, idleTimeoutMinutes),
      })),
    // `tick` is deliberately a dependency: it is what re-runs statusOf on the
    // clock, since crossing the idle threshold produces no socket event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sorted, idleTimeoutMinutes, tick]
  );

  const counts = useMemo(
    () => ({
      all: withStatus.length,
      online: withStatus.filter(({ status }) => status !== "offline").length,
      offline: withStatus.filter(({ status }) => status === "offline").length,
    }),
    [withStatus]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return withStatus
      .filter(({ status }) =>
        filter === "all"
          ? true
          : filter === "online"
          ? status !== "offline"
          : status === "offline"
      )
      .filter(({ user }) => (q ? fullName(user).toLowerCase().includes(q) : true));
  }, [withStatus, filter, query]);

  const tabs = [
    { key: "all", label: "All", n: counts.all },
    { key: "online", label: "Online", n: counts.online },
    { key: "offline", label: "Offline", n: counts.offline },
  ];

  return (
    <PanelShell
      open={activeModal}
      onClose={() => setActiveModal(false)}
      title="Team"
      count={counts.online || null}
      tabs={tabs}
      activeTab={filter}
      onTab={setFilter}
      query={query}
      onQuery={setQuery}
      searchPlaceholder="Search people"
    >
      {visible.length === 0 && (
        <div className="np-none">
          <b>{counts.all === 0 ? "No sessions yet" : "No matches"}</b>
          {counts.all === 0
            ? "Sign-ins will appear here."
            : "Try a different tab or search."}
        </div>
      )}

      {/* Split rather than one long list: 40 identical "Offline" rows buried
          the person who is actually online. */}
      {["active", "idle", "offline"].map((bucket) => {
        const rows = visible.filter(({ status }) => status === bucket);
        if (!rows.length) return null;
        return (
          <React.Fragment key={bucket}>
            <div className="np-day">
              {bucket === "active"
                ? `Active — ${rows.length}`
                : bucket === "idle"
                ? `Idle — ${rows.length}`
                : `Offline — ${rows.length}`}
            </div>
            {rows.map(({ user, status }, j) => {
              const rowClass =
                status === "active"
                  ? "is-online"
                  : status === "idle"
                  ? "is-idle"
                  : "is-off";
              return (
                <div
                  key={user.id ?? `${bucket}-${j}`}
                  className={`np-row ${rowClass}`}
                >
                  <span className={status === "offline" ? "np-avatar-dim" : ""}>
                    <PanelAvatar
                      firstName={user?.employee?.firstName}
                      lastName={user?.employee?.lastName}
                      status={status}
                    />
                  </span>
                  <div className="np-main">
                    <div className="np-line1">
                      <span className="np-who">{fullName(user)}</span>
                      <span className="np-when">
                        {shortWhen(
                          status === "offline"
                            ? user.logoutTime
                            : user.lastActivityAt || user.loginTime
                        )}
                      </span>
                    </div>
                    <div className="np-line2">
                      <span className={`np-dot${status !== "offline" ? ` ${status === "idle" ? "idle" : "on"}` : ""}`} />
                      {STATUS_LABEL[status]}
                    </div>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </PanelShell>
  );
};
